// =============================================================================
// ToDoPage.jsx
// =============================================================================
// Responder task queue — the main task list for staff in "responder" mode.
// Shows new_request / new_comment / new_file tasks with filters, assignment,
// release, completion, and inline notes.
//
// Props:
//   onViewRequest  — fn(requestId, refNo) — navigate to a request
// Context:
//   user           — current user object, from useSession
//   requests       — cached request list (used for "Staged" badge lookup), from useRequests
//
// Extracted from DashboardPages.jsx (May 2026).
// =============================================================================

import React from "react";
import { isAdminRole } from "../../app/access";
import { useRequests } from "../../app/RequestsContext.jsx";
import { useSession } from "../../app/SessionContext.jsx";
import { useRepeatCheckSettings } from "../../app/UiConfigContext.jsx";
import { listAssignableStaff } from "../../data/profiles";
import { insertInternalNote } from "../../data/requestInternalNotes";
import {
  listResponderTasks,
  NON_RESPONDER_TYPES,
  updateResponderTasksByRequest,
} from "../../data/requestTasks";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { useFitViewportHeight } from "../../hooks/useFitViewportHeight";
import { useLiveList } from "../../hooks/useLiveList";
import { usePageSize } from "../../hooks/usePageSize";
import { useRepeatFlags } from "../../hooks/useRepeatFlags";
import { confirmDialog } from "../../services/dialogService";
import { groupAndOrderTasks } from "../../services/taskGrouping";
import { sendEmail } from "../../utils/actions";
import { logAudit } from "../../utils/auditLog";
import { REQUEST_STATUSES } from "../../utils/enums";
import { fmtDateTimeLocal, formatRef } from "../../utils/helpers";
import { PageNav } from "../common/PageNav.jsx";
import { RequestRefLink } from "../common/RequestRefLink.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";
import { HelpButton } from "../form/HelpButton.jsx";
import { RepeatListChip } from "../request/RepeatFlagPanel.jsx";

// Rows per page is the shared per-device preference (services/pageSize.ts) — the
// old fixed 25 is gone, John's call 6 Aug: no per-screen exceptions.

// ── Atoms (private to this file) ────────────────────────────────────────────
const TASK_LABELS = {
  new_request: "New Request",
  new_comment: "New Comment",
  new_file: "New Document",
  device: "Device",
  stationery_qld: "Stationery Pack",
};
const TASK_COLOURS = {
  new_request: { bg: "#E8F5E9", text: "#2E7D32", border: "#A5D6A7" },
  new_comment: { bg: "#E3F2FD", text: "#1565C0", border: "#90CAF9" },
  new_file: { bg: "#FFF3E0", text: "#E65100", border: "#FFCC80" },
  device: { bg: "#E3F2FD", text: "#0C447C", border: "#90CAF9" },
  stationery_qld: { bg: "#E8F5E9", text: "#1B5E20", border: "#A5D6A7" },
};

// Types the Responders list never shows (picker-only + the top-up approval queue). Used
// as the useLiveList scope filter so a picker/top-up change never lands in this list —
// it mirrors the server-side exclusion in listResponderTasks.
// Single source of truth (data/requestTasks.ts) — picker queue + top-up queue types.
// Keeps the Responders live-list rowBelongs in lockstep with the data-layer exclusions.
const RESPONDER_EXCLUDED_TYPES = new Set(NON_RESPONDER_TYPES);

// ─────────────────────────────────────────────────────────────────────────────
// TaskBadge — colour-coded badge for a responder task type
// ─────────────────────────────────────────────────────────────────────────────
// Renders: a small pill with the human label + colour for the given type
//          (new_request / new_comment / new_file / device / stationery_qld).
// Props:
//   type - task type string (matches TASK_LABELS / TASK_COLOURS keys)
function TaskBadge({ type }) {
  const c = TASK_COLOURS[type] || { bg: "#F5F5F5", text: "#555", border: "#DDD" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 700,
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {TASK_LABELS[type] || type}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ToDoPage — responder task queue
// ─────────────────────────────────────────────────────────────────────────────
function ToDoPage({ onViewRequest }) {
  const [TASK_PAGE_SIZE] = usePageSize();
  // `user` + `requests` now come from context rather than props (Phase 3 refactor).
  const { user } = useSession();
  const { requests, loadUnactionedTasks } = useRequests();
  // Supervisor (admin/superuser) — may act on any responder's task; standard
  // Responders are limited to their own (see the per-row permission gates below).
  const isSupervisor = isAdminRole(user);
  // Filters persist across navigation (e.g. opening a task and coming back)
  // by keeping them in sessionStorage. Browser close clears them.
  function loadFilter(key, fallback) {
    try {
      const v = sessionStorage.getItem(`zephyr_todo_${key}`);
      return v == null ? fallback : v;
    } catch (_e) {
      return fallback;
    }
  }
  function saveFilter(key, value) {
    try {
      sessionStorage.setItem(`zephyr_todo_${key}`, value);
    } catch (_e) {}
  }
  // Back-compat: the status-filter tokens were renamed (pending -> unactioned,
  // completed -> actioned, 2026-06-27) so the task-queue's "done"/"not done"
  // vocabulary stops colliding with the item-approval status "Pending" and the
  // (reserved) future Request status "Completed". Map any value a returning user
  // saved under the old names so their persisted filter still resolves.
  function normaliseStatusFilter(v) {
    if (v === "pending") return "unactioned";
    if (v === "completed") return "actioned";
    return v;
  }
  // The task LIST is now live (C3): Realtime on request_tasks with delta-apply, plus the
  // hook's own fallback poll + reconnect reconcile. `tasks` and `loading` come from it.
  const {
    rows: tasks,
    loading,
    refresh,
    applyChange,
  } = useLiveList({
    table: "request_tasks",
    fetch: listResponderTasks,
    rowBelongs: function (t) {
      return !RESPONDER_EXCLUDED_TYPES.has(t.type);
    },
    sort: function (a, b) {
      // Newest first — matches listResponderTasks' created_at DESC ordering.
      return (b.created_at || "").localeCompare(a.created_at || "");
    },
  });
  // Possible-repeat chips (db-211): one call for every request on the list, re-run only
  // when the set of request ids changes. This page is staff-only.
  const repeatSettings = useRepeatCheckSettings();
  const { flags: repeatFlags } = useRepeatFlags(
    (tasks || []).map(function (t) {
      return t.request_id;
    }),
    { enabled: true, months: repeatSettings.months, types: repeatSettings.types }
  );
  const [filterType, setFilterTypeRaw] = React.useState(function () {
    return loadFilter("type", "all");
  });
  const [filterState, setFilterStateRaw] = React.useState(function () {
    return loadFilter("state", "all");
  });
  const [filterStatus, setFilterStatusRaw] = React.useState(function () {
    return normaliseStatusFilter(loadFilter("status", "unactioned"));
  });
  const [filterStaff, setFilterStaffRaw] = React.useState(function () {
    return loadFilter("staff", "all");
  });
  const [search, setSearchRaw] = React.useState(function () {
    return loadFilter("search", "");
  });
  // Wrapped setters that mirror to sessionStorage
  function setFilterType(v) {
    saveFilter("type", v);
    setFilterTypeRaw(v);
  }
  function setFilterState(v) {
    saveFilter("state", v);
    setFilterStateRaw(v);
  }
  function setFilterStatus(v) {
    saveFilter("status", v);
    setFilterStatusRaw(v);
  }
  function setFilterStaff(v) {
    saveFilter("staff", v);
    setFilterStaffRaw(v);
  }
  function setSearch(v) {
    saveFilter("search", v);
    setSearchRaw(v);
  }
  // Staff roster for the "Assign Other" modal, loaded once. Each entry is the
  // identity triple { id, display_name, staff_user_email }, so the assign action
  // carries the assignee's profile ID (never a mutable display string) and resolves
  // the recipient email from it. Replaces the old display-name roster, which caused
  // the assignment email to be silently skipped whenever the carried name differed
  // from profiles.display_name (requests 2026-03081 / 03082).
  const [assignableStaff, setAssignableStaff] = React.useState([]);
  React.useEffect(function () {
    listAssignableStaff().then(function (rows) {
      setAssignableStaff(rows || []);
    });
  }, []);
  // Assign / Release operate on a whole request group: { requestId, refNo,
  // shelterName, openTasks }.
  const [assignGroup, setAssignGroup] = React.useState(null);
  const [assignTarget, setAssignTarget] = React.useState("");
  const [assignNote, setAssignNote] = React.useState("");
  const [releaseGroup, setReleaseGroup] = React.useState(null);
  const [releaseNote, setReleaseNote] = React.useState("");
  const [page, setPage] = React.useState(0);
  const assignNoteId = React.useId();
  const releaseNoteId = React.useId();

  // Slim badge poll (C3, pragmatic): the task LIST is live via useLiveList (Realtime + its
  // own fallback poll), so this poll exists ONLY to keep the nav badge count fresh — A2's
  // behaviour preserved without deriving the badge through app context. Deduped in
  // usePortalData; guarded for test providers that don't supply the loader.
  useAutoRefresh(function () {
    if (loadUnactionedTasks) loadUnactionedTasks();
  });

  // The Responders list acts at the REQUEST level: every action below cascades
  // to all open responder tasks of the request (Assign to Me / Assign Other /
  // Release / Done), via updateResponderTasksByRequest.
  // `group` = { requestId, refNo, shelterName, openTasks }.

  // Patch the locally-held rows the cascade returned so the table reflects the change
  // without waiting for the Realtime round-trip. Folds each returned row through the SAME
  // delta path a Realtime UPDATE uses, so Realtime re-applying it later is idempotent. (C3)
  function applyTaskUpdates(rows) {
    if (!rows || rows.length === 0) return;
    rows.forEach(function (r) {
      applyChange({ eventType: "UPDATE", new: r });
    });
  }

  async function takeOnGroup(group) {
    const now = new Date().toISOString();
    const { data, error } = await updateResponderTasksByRequest(
      group.requestId,
      // assigned_to (text) is filled server-side by trg_sync_request_task_assignee_name
      // (db-202) from assigned_to_id's profile — the client no longer sends the name.
      { assigned_to_id: user.id, assigned_at: now },
      "open"
    );
    if (!error) {
      applyTaskUpdates(data);
      logAudit(
        "task_taken_on",
        { request_id: group.requestId, ref: group.refNo, by: user.shelterName },
        group.requestId
      );
      // Take On = also open the request straight away.
      if (onViewRequest) onViewRequest(group.requestId, group.refNo);
    }
  }

  async function assignGroupTo(group, profileId, note) {
    const now = new Date().toISOString();
    // Resolve the assignee from the loaded roster BY ID — never by display name.
    // The old path looked the person up by the carried name string
    // (getProfileByDisplayName → .eq("display_name", …)), which returned nothing
    // whenever that name (the attributed "(Zephyr)" short form, user.shelterName)
    // differed from profiles.display_name — so assigned_to_id saved null AND the
    // notification email was silently skipped (requests 2026-03081 / 03082).
    // Keying on the profile id makes both reliable regardless of display values.
    const assignee = assignableStaff.find(function (s) {
      return s.id === profileId;
    });
    const assigneeName = assignee?.display_name || "";
    const assigneeEmail = assignee?.staff_user_email;
    const { data, error } = await updateResponderTasksByRequest(
      group.requestId,
      // assigned_to (text) is derived server-side from assigned_to_id (db-202);
      // assigneeName is still used below for the audit/note/email only.
      { assigned_to_id: profileId, assigned_at: now },
      "open"
    );
    if (!error) {
      applyTaskUpdates(data);
      logAudit(
        "task_assigned",
        {
          request_id: group.requestId,
          ref: group.refNo,
          to: assigneeName,
          by: user.shelterName,
          note: note || "",
        },
        group.requestId
      );
      // Silent INTERNAL note recording the assignment reason (db-122). Staff-only
      // table — a shelter can never read it (RLS row-level deny). Does NOT send an
      // email and is separate from the shelter comment thread. Best-effort.
      if ((note || "").trim()) {
        insertInternalNote({
          requestId: group.requestId,
          author: user.shelterName,
          authorId: user.id || null,
          text: `Assigned to ${assigneeName}: ${note.trim()}`,
          kind: "assign",
        });
      }
      // One email to the assignee for the whole request (not one per task). The
      // note is delivered in that email.
      if (assigneeEmail) {
        sendEmail("task_assigned", {
          requestId: group.requestId,
          assigneeName: assigneeName,
          assigneeEmail: assigneeEmail,
          refNo: group.refNo || "",
          shelterName: group.shelterName || "",
          taskType: "",
          assignedBy: user.shelterName,
          note: note || "",
        });
      }
    }
    setAssignGroup(null);
    setAssignTarget("");
    setAssignNote("");
  }

  async function releaseGroupTasks(group, note) {
    const { data, error } = await updateResponderTasksByRequest(
      group.requestId,
      // Nulling assigned_to_id clears assigned_to (text) via trg_sync_request_task_assignee_name (db-202).
      { assigned_to_id: null, assigned_at: null, release_note: note || null },
      "open"
    );
    if (!error) {
      applyTaskUpdates(data);
      logAudit(
        "task_released",
        { request_id: group.requestId, ref: group.refNo, by: user.shelterName, note: note || "" },
        group.requestId
      );
      // Silent INTERNAL note recording the release reason (db-122) — mirrors the
      // assign path so the two staff notes live in one place (request_internal_notes,
      // kind "release"/"assign"). Staff-only via RLS; best-effort; also kept on
      // request_tasks.release_note above for the task-row hover text.
      if ((note || "").trim()) {
        insertInternalNote({
          requestId: group.requestId,
          author: user.shelterName,
          authorId: user.id || null,
          text: `Released — ${note.trim()}`,
          kind: "release",
        });
      }
    }
    setReleaseGroup(null);
    setReleaseNote("");
  }

  async function completeGroup(group) {
    const n = group.openTasks.length;
    const ok = await confirmDialog({
      title: "Mark actioned",
      message: `Mark all ${n} task${n !== 1 ? "s" : ""} for ${formatRef(group.refNo)} as actioned?`,
      confirmLabel: "Mark actioned",
    });
    if (!ok) return;
    const now = new Date().toISOString();
    const { data, error } = await updateResponderTasksByRequest(
      group.requestId,
      { completed_at: now },
      "open"
    );
    if (!error) {
      applyTaskUpdates(data);
      logAudit(
        "task_actioned",
        { request_id: group.requestId, ref: group.refNo, by: user.shelterName },
        group.requestId
      );
    }
  }

  function fmtDate(iso) {
    if (!iso) return "-";
    return fmtDateTimeLocal(iso) || "-";
  }

  // Derive staff list for filter
  const staffList = Array.from(
    new Set(
      tasks
        .filter(function (t) {
          return t.assigned_to;
        })
        .map(function (t) {
          return t.assigned_to;
        })
    )
  ).sort();
  const stateList = Array.from(
    new Set(
      tasks
        .filter(function (t) {
          return t.state;
        })
        .map(function (t) {
          return t.state;
        })
    )
  ).sort();

  // Drafts belong only in the Shelter view, never in the Responders Task List.
  // This guard is intentionally a self-contained copy — the Pickers list has its
  // own equivalent. Keeping them independent (rather than sharing one helper)
  // means a future change to either list can't cause collateral damage to the
  // other.
  function taskIsForDraftRequest(t) {
    if (t.ref_no?.startsWith("DRAFT")) return true;
    const linked = requests?.find(function (r) {
      return r.id === t.request_id || r.refNo === t.ref_no;
    });
    return !!(
      linked &&
      (linked.status === REQUEST_STATUSES.DRAFT || linked.refNo?.startsWith("DRAFT"))
    );
  }
  const nonDraftTasks = tasks.filter(function (t) {
    return !taskIsForDraftRequest(t);
  });

  // A responder task is "mine" strictly by assignee id. assigned_to_id is the enforced
  // source of truth (db-201 FK + db-202 name-sync trigger; assigned_to text is now a
  // derived cache). The old `|| t.assigned_to === user.shelterName` name fallback was
  // removed with T2a — it matched the attributed "(Zephyr)" short form, never the
  // derived display_name, and was the fragility the assignee-by-id work set out to kill.
  const isMineTask = (t) => !!t.assigned_to_id && t.assigned_to_id === user.id;

  const filtered = nonDraftTasks.filter(function (t) {
    if (filterType !== "all" && t.type !== filterType) return false;
    if (filterState !== "all" && t.state !== filterState) return false;
    if (filterStaff !== "all" && t.assigned_to !== filterStaff) return false;
    // "unactioned"/"actioned" are the task-queue's not-done/done filters; they key
    // off the completed_at timestamp column (which is intentionally NOT renamed).
    if (filterStatus === "unactioned" && t.completed_at) return false;
    if (filterStatus === "actioned" && !t.completed_at) return false;
    if (filterStatus === "assigned" && (!t.assigned_to || t.completed_at)) return false;
    if (filterStatus === "unassigned" && (t.assigned_to || t.completed_at)) return false;
    // "assigned_me" — assigned to the current responder and not yet actioned (mirrors
    // the Assigned ME card count + the row-level "is this mine?" check).
    if (filterStatus === "assigned_me" && (t.completed_at || !isMineTask(t))) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (
        !(t.ref_no || "").toLowerCase().includes(q) &&
        !(t.shelter_name || "").toLowerCase().includes(q) &&
        !(t.description || "").toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  // Paginate the ordered task rows (25/page). Reset to page 1 when the filter/
  // search result size changes so a paged view can't strand on an out-of-range page.
  const orderedRows = groupAndOrderTasks(filtered);
  React.useEffect(() => setPage(0), [orderedRows.length]);
  const taskPageCount = Math.ceil(orderedRows.length / TASK_PAGE_SIZE);
  const taskSafePage = Math.min(page, Math.max(0, taskPageCount - 1));
  const taskPageRows = orderedRows.slice(
    taskSafePage * TASK_PAGE_SIZE,
    (taskSafePage + 1) * TASK_PAGE_SIZE
  );
  // The rows scroll inside a viewport-fit box (sticky column headers); the filters
  // + column headers stay put. Measured once the table renders.
  const listScrollRef = React.useRef(null);
  const listMaxH = useFitViewportHeight(listScrollRef, {
    enabled: !loading && orderedRows.length > 0,
  });

  const unactioned = nonDraftTasks.filter(function (t) {
    return !t.completed_at;
  }).length;
  const unassigned = nonDraftTasks.filter(function (t) {
    return !t.assigned_to && !t.completed_at;
  }).length;
  const actioned = nonDraftTasks.filter(function (t) {
    return !!t.completed_at;
  }).length;
  // Assigned (All) = taken on by any responder but not yet actioned (matches the
  // "assigned" filter). Unassigned + Assigned All = Unactioned.
  const assigned = nonDraftTasks.filter(function (t) {
    return !!t.assigned_to && !t.completed_at;
  }).length;
  // Assigned ME = assigned to the current responder + not yet actioned (mirrors the
  // "is this mine?" check used on the task rows). A subset of Assigned All.
  const assignedMe = nonDraftTasks.filter(function (t) {
    return !t.completed_at && isMineTask(t);
  }).length;

  const selStyle = "py-[6px] px-[10px] rounded-[6px] border border-[#ccc] text-[13px] bg-white";
  const thStyle =
    "px-[10px] py-2 text-left text-[11px] font-bold uppercase tracking-[0.5px] text-[#607D79] border-b-2 border-[#E0E0E0] whitespace-nowrap sticky top-0 z-[1] bg-[#FAFAFA]";
  const tdStyle =
    "px-[10px] py-[7px] text-[13px] border-b border-[#F0F0F0] align-middle overflow-hidden text-ellipsis whitespace-nowrap";
  // Actions cell: no clipping (buttons may wrap to a second line rather than be cut off).
  const actionsTdStyle = "px-[10px] py-[7px] text-[13px] border-b border-[#F0F0F0] align-middle";

  return (
    <div className="px-7 py-6 max-w-[var(--page-max)] mx-auto">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ margin: 0, color: "#00695C", fontSize: 22 }}>Responders Task List</h2>
          <div style={{ fontSize: 13, color: "#607D79", marginTop: 2 }}>Responders task queue</div>
        </div>
        <button
          onClick={function () {
            refresh();
          }}
          style={{
            background: "white",
            border: "1px solid #ccc",
            borderRadius: 7,
            padding: "7px 14px",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Stats */}
      <div
        style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 16, marginBottom: 20 }}
      >
        {[
          { label: "Unassigned", value: unassigned, colour: "#E53935", status: "unassigned" },
          { label: "Assigned ME", value: assignedMe, colour: "#5E35B1", status: "assigned_me" },
          { label: "Assigned All", value: assigned, colour: "#1E88E5", status: "assigned" },
          { label: "Unactioned", value: unactioned, colour: "#FFB300", status: "unactioned" },
          { label: "Actioned", value: actioned, colour: "#00897B", status: "actioned" },
        ].map(function (s) {
          // Quick-link filter buttons — click to filter the list to that subset, click
          // again to clear (mirrors the All Requests / AdminDash cards). John, 27 Aug 2026.
          const active = filterStatus === s.status;
          return (
            <button
              key={s.label}
              type="button"
              aria-label={`Filter by ${s.label}`}
              onClick={function () {
                setFilterStatus(active ? "all" : s.status);
              }}
              style={{
                background: "white",
                borderRadius: 10,
                padding: "10px 20px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                border: active ? `2px solid ${s.colour}` : "2px solid transparent",
                cursor: "pointer",
                fontFamily: "inherit",
                width: "100%",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
              >
                <div style={{ fontSize: 36, fontWeight: 700, color: s.colour }}>{s.value}</div>
                <div style={{ fontSize: 20, color: "#607D79" }}>{s.label}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div
        style={{
          background: "white",
          borderRadius: 10,
          padding: "14px 16px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
          marginBottom: 16,
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          alignItems: "center",
        }}
      >
        <input
          value={search}
          onChange={function (e) {
            setSearch(e.target.value);
          }}
          placeholder="Search ref, DFV Service, description..."
          style={{
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #ccc",
            fontSize: 13,
            minWidth: 220,
          }}
        />
        <select
          value={filterStatus}
          onChange={function (e) {
            setFilterStatus(e.target.value);
          }}
          className={selStyle}
        >
          <option value="all">All statuses</option>
          <option value="unactioned">Unactioned</option>
          <option value="unassigned">Unassigned</option>
          <option value="assigned">Assigned</option>
          <option value="assigned_me">Assigned to me</option>
          <option value="actioned">Actioned</option>
        </select>
        <select
          value={filterType}
          onChange={function (e) {
            setFilterType(e.target.value);
          }}
          className={selStyle}
        >
          <option value="all">All types</option>
          <option value="new_request">New Request</option>
          <option value="new_comment">New Comment</option>
          <option value="new_file">New Document</option>
        </select>
        <select
          value={filterState}
          onChange={function (e) {
            setFilterState(e.target.value);
          }}
          className={selStyle}
        >
          <option value="all">All states</option>
          {stateList.map(function (s) {
            return (
              <option key={s} value={s}>
                {s}
              </option>
            );
          })}
        </select>
        <select
          value={filterStaff}
          onChange={function (e) {
            setFilterStaff(e.target.value);
          }}
          className={selStyle}
        >
          <option value="all">All staff</option>
          {staffList.map(function (s) {
            return (
              <option key={s} value={s}>
                {s}
              </option>
            );
          })}
        </select>
        <span style={{ fontSize: 12, color: "#607D79", marginLeft: "auto" }}>
          {filtered.length} task{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Table */}
      <div
        style={{
          background: "white",
          borderRadius: 10,
          boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#607D79" }}>Loading tasks...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#607D79" }}>
            {filterStatus === "unactioned"
              ? "🎉 No unactioned tasks!"
              : "No tasks match your filters."}
          </div>
        ) : (
          <div
            ref={listScrollRef}
            style={
              listMaxH
                ? { overflowX: "auto", overflowY: "auto", maxHeight: listMaxH }
                : { overflowX: "auto" }
            }
          >
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr style={{ background: "#FAFAFA" }}>
                  <th className={thStyle} style={{ width: "9%" }}>
                    Ref
                  </th>
                  <th className={thStyle} style={{ width: "16%" }}>
                    DFV Service /Code
                  </th>
                  <th className={thStyle} style={{ width: "9%" }}>
                    Type
                  </th>
                  <th className={thStyle} style={{ width: "11%" }}>
                    Created
                  </th>
                  <th className={thStyle} style={{ width: "13%" }}>
                    Assigned To
                  </th>
                  <th className={thStyle} style={{ width: "11%" }}>
                    Actioned
                  </th>
                  <th className={thStyle} style={{ width: "18%" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      Actions
                      <HelpButton helpKey="task_actions" user={user} />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {taskPageRows.map(function (row) {
                  const task = row.task;
                  const isFirst = row.isFirstInGroup;
                  const grouped = row.groupSize > 1;
                  const isCompleted = !!task.completed_at;
                  const isMine = isMineTask(task);
                  // Group aggregate — action buttons render once (first row) and
                  // act on every OPEN responder task of the request.
                  const openTasks = row.groupTasks.filter(function (t) {
                    return !t.completed_at;
                  });
                  const group = {
                    requestId: task.request_id,
                    refNo: task.ref_no,
                    shelterName: task.shelter_name,
                    openTasks: openTasks,
                  };
                  const hasOpen = openTasks.length > 0;
                  const hasUnassigned = openTasks.some(function (t) {
                    return !t.assigned_to;
                  });
                  const hasAssigned = openTasks.some(function (t) {
                    return !!t.assigned_to;
                  });
                  // ── Permission gates (UI) ────────────────────────────────────
                  // Supervisors (admin/superuser) may act on anyone's task;
                  // standard Responders are restricted to their own. `openMine` =
                  // the group is assigned to me; `openAssignedToOther` = it's held
                  // by a different responder.
                  const openMine = openTasks.some(isMineTask);
                  const openAssignedToOther = openTasks.some(function (t) {
                    return t.assigned_to && !isMineTask(t);
                  });
                  // Actioned: ONLY the assignee — no supervisor override (John, 11 Jul).
                  const canActioned = openMine;
                  // Release own; a supervisor may release anyone's.
                  const canRelease = isSupervisor || openMine;
                  // Reassign / take over another responder's task = supervisor only;
                  // an unassigned (or own) task can be assigned/taken by anyone.
                  const canTakeOver = isSupervisor || !openAssignedToOther;
                  // Request-level buttons render once, on the first OPEN row of the
                  // group (never on a faded/actioned row, e.g. in the All/Actioned
                  // filter). A fully-actioned group shows no action buttons.
                  const isButtonRow = hasOpen && task.id === openTasks[0].id;
                  // Stacking: repeat rows greyed; first row of a multi-task group
                  // gets a heavier top border to delimit the stack.
                  const rowStyle = {
                    opacity: isCompleted ? 0.5 : 1,
                    background: grouped ? (isFirst ? "#F7FBFA" : "#FBFDFC") : undefined,
                    borderTop: grouped && isFirst ? "2px solid #E0E0E0" : undefined,
                  };
                  return (
                    <tr key={task.id} style={rowStyle}>
                      <td className={tdStyle}>
                        {!isFirst ? (
                          <span style={{ color: "#B0BEC5", paddingLeft: 10 }}>↳</span>
                        ) : task.ref_no ? (
                          <div>
                            <RequestRefLink
                              href={`/requests/${task.request_id}`}
                              refNo={task.ref_no}
                              onOpen={() => {
                                if (onViewRequest) onViewRequest(task.request_id, task.ref_no);
                              }}
                              style={{
                                color: "#00897B",
                                fontWeight: 700,
                                cursor: "pointer",
                                fontSize: 13,
                                textDecoration: "underline",
                              }}
                            />
                            <RepeatListChip flag={repeatFlags[String(task.request_id)]} />
                            {(function () {
                              const matchReq = requests?.find(function (r) {
                                return r.id === task.request_id || r.refNo === task.ref_no;
                              });
                              if (
                                matchReq &&
                                matchReq.approvalsConfirmedAt === null &&
                                (matchReq.status === REQUEST_STATUSES.PART_PROCESSED ||
                                  matchReq.status === REQUEST_STATUSES.FULLY_PROCESSED)
                              ) {
                                return (
                                  <div
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color: "#7c4a00",
                                      background: "#FFF8E1",
                                      border: "1px solid #FFB300",
                                      borderRadius: 3,
                                      padding: "1px 5px",
                                      marginTop: 3,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Staged
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className={tdStyle}>
                        {isFirst ? (
                          <div>
                            <span style={{ fontWeight: 600 }}>{task.shelter_name || "-"}</span>
                            {task.shelter_code && (
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#00695C",
                                  fontWeight: 600,
                                  marginTop: 2,
                                }}
                              >
                                {task.shelter_code}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </td>
                      <td className={tdStyle}>
                        <TaskBadge type={task.type} />
                      </td>
                      <td className={tdStyle}>{fmtDate(task.created_at)}</td>
                      <td className={tdStyle}>
                        {task.assigned_to ? (
                          <span>
                            <span
                              style={{
                                fontWeight: isMine ? 700 : 400,
                                color: isMine ? "#00695C" : "inherit",
                                display: "block",
                              }}
                            >
                              {task.assigned_to}
                              {isMine ? " (you)" : ""}
                            </span>
                            {task.assigned_at && (
                              <span style={{ fontSize: 11, color: "#607D79" }}>
                                {fmtDate(task.assigned_at)}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span>
                            <span
                              style={{ color: "#607D79", fontStyle: "italic", display: "block" }}
                            >
                              Unassigned
                            </span>
                            {task.release_note && !task.completed_at && (
                              <span
                                style={{ fontSize: 11, color: "#607D79", fontStyle: "italic" }}
                                title={`Release note: ${task.release_note}`}
                              >
                                Released:{" "}
                                {task.release_note.length > 30
                                  ? `${task.release_note.slice(0, 30)}…`
                                  : task.release_note}
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className={tdStyle}>{fmtDate(task.completed_at)}</td>
                      <td className={actionsTdStyle}>
                        {isButtonRow ? (
                          <div
                            style={{
                              display: "flex",
                              gap: 4,
                              alignItems: "center",
                              flexWrap: "wrap",
                              rowGap: 4,
                            }}
                          >
                            {hasUnassigned ? (
                              <button
                                onClick={function () {
                                  takeOnGroup(group);
                                }}
                                disabled={!canTakeOver}
                                title={
                                  canTakeOver
                                    ? undefined
                                    : "Only a supervisor can take over another responder's task"
                                }
                                style={{
                                  fontSize: 11,
                                  padding: "3px 7px",
                                  borderRadius: 5,
                                  cursor: canTakeOver ? "pointer" : "not-allowed",
                                  whiteSpace: "nowrap",
                                  background: "#E0F2F1",
                                  border: `1px solid ${"#00897B"}40`,
                                  color: "#00695C",
                                  fontWeight: 600,
                                  opacity: canTakeOver ? 1 : 0.45,
                                }}
                              >
                                Assign Me
                              </button>
                            ) : null}
                            {hasAssigned ? (
                              <button
                                onClick={function () {
                                  setReleaseGroup(group);
                                  setReleaseNote("");
                                }}
                                disabled={!canRelease}
                                title={
                                  canRelease
                                    ? undefined
                                    : "Only a supervisor can release another responder's task"
                                }
                                style={{
                                  fontSize: 11,
                                  padding: "3px 7px",
                                  borderRadius: 5,
                                  cursor: canRelease ? "pointer" : "not-allowed",
                                  whiteSpace: "nowrap",
                                  background: "#FFF3E0",
                                  border: "1px solid #FFCC80",
                                  color: "#E65100",
                                  opacity: canRelease ? 1 : 0.45,
                                }}
                              >
                                Release
                              </button>
                            ) : null}
                            <button
                              onClick={function () {
                                setAssignGroup(group);
                                setAssignTarget("");
                                setAssignNote("");
                              }}
                              disabled={!canTakeOver}
                              title={
                                canTakeOver
                                  ? undefined
                                  : "Only a supervisor can reassign another responder's task"
                              }
                              style={{
                                fontSize: 11,
                                padding: "3px 7px",
                                borderRadius: 5,
                                cursor: canTakeOver ? "pointer" : "not-allowed",
                                whiteSpace: "nowrap",
                                background: "#EDE8FA",
                                border: "1px solid #C4B5FD",
                                color: "#534AB7",
                                fontWeight: 600,
                                opacity: canTakeOver ? 1 : 0.45,
                              }}
                            >
                              Assign Other
                            </button>
                            <button
                              onClick={function () {
                                completeGroup(group);
                              }}
                              disabled={!canActioned}
                              title={
                                canActioned
                                  ? undefined
                                  : "Only the responder this task is assigned to can mark it actioned"
                              }
                              style={{
                                fontSize: 11,
                                padding: "3px 7px",
                                borderRadius: 5,
                                cursor: canActioned ? "pointer" : "not-allowed",
                                whiteSpace: "nowrap",
                                background: "#E8F5E9",
                                border: "1px solid #A5D6A7",
                                color: "#2E7D32",
                                fontWeight: 600,
                                opacity: canActioned ? 1 : 0.45,
                              }}
                            >
                              ✓ Actioned
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && orderedRows.length > 0 && (
          <PageNav page={taskSafePage} pageCount={taskPageCount} onPage={setPage} />
        )}
      </div>

      {/* Assign to modal */}
      {assignGroup && (
        <ModalBackdrop
          onClose={function () {
            setAssignGroup(null);
          }}
          contentStyle={{
            background: "white",
            borderRadius: 12,
            padding: 24,
            width: 360,
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 15, color: "#00695C", marginBottom: 14 }}>
            Assign request
          </div>
          <div style={{ fontSize: 13, color: "#607D79", marginBottom: 10 }}>
            {formatRef(assignGroup.refNo)} — assigns all {assignGroup.openTasks.length} open task
            {assignGroup.openTasks.length !== 1 ? "s" : ""} for this request.
          </div>
          <select
            value={assignTarget}
            onChange={function (e) {
              setAssignTarget(e.target.value);
            }}
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 7,
              border: `1px solid ${"#CFE0DE"}`,
              fontSize: 14,
              marginBottom: 14,
              fontFamily: "inherit",
            }}
          >
            <option value="">— Select person —</option>
            {assignableStaff.map(function (s) {
              return (
                <option key={s.id} value={s.id}>
                  {s.display_name}
                </option>
              );
            })}
          </select>
          <label
            htmlFor={assignNoteId}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#1A2E2B",
              display: "block",
              marginBottom: 6,
            }}
          >
            Note for assignee (required, min 4 characters)
          </label>
          <textarea
            id={assignNoteId}
            value={assignNote}
            onChange={function (e) {
              setAssignNote(e.target.value);
            }}
            placeholder="e.g. Please prioritise — DFV Service is waiting on uniforms before school start..."
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 7,
              border: `1px solid ${"#CFE0DE"}`,
              fontSize: 13,
              fontFamily: "inherit",
              resize: "vertical",
              minHeight: 70,
              boxSizing: "border-box",
              marginBottom: 14,
            }}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              onClick={function () {
                setAssignGroup(null);
              }}
              style={{
                background: "#eee",
                border: "none",
                borderRadius: 6,
                padding: "7px 16px",
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              Cancel
            </button>
            <button
              onClick={function () {
                if (assignTarget && assignNote.trim().length >= 4) {
                  assignGroupTo(assignGroup, assignTarget, assignNote);
                }
              }}
              disabled={!assignTarget || assignNote.trim().length < 4}
              style={{
                background: "#00897B",
                color: "white",
                border: "none",
                borderRadius: 6,
                padding: "7px 16px",
                cursor: assignTarget && assignNote.trim().length >= 4 ? "pointer" : "not-allowed",
                fontSize: 13,
                fontWeight: 600,
                opacity: assignTarget && assignNote.trim().length >= 4 ? 1 : 0.5,
              }}
            >
              Assign
            </button>
          </div>
        </ModalBackdrop>
      )}

      {/* Release with note modal */}
      {releaseGroup && (
        <ModalBackdrop
          onClose={function () {
            setReleaseGroup(null);
          }}
          contentStyle={{
            background: "white",
            borderRadius: 12,
            padding: 24,
            width: 400,
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 15, color: "#00695C", marginBottom: 14 }}>
            Release request
          </div>
          <div style={{ fontSize: 13, color: "#607D79", marginBottom: 10 }}>
            {formatRef(releaseGroup.refNo)} — releases all {releaseGroup.openTasks.length} open task
            {releaseGroup.openTasks.length !== 1 ? "s" : ""} for this request.
          </div>
          <label
            htmlFor={releaseNoteId}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#1A2E2B",
              display: "block",
              marginBottom: 6,
            }}
          >
            Reason for releasing (required, min 4 characters)
          </label>
          <textarea
            id={releaseNoteId}
            value={releaseNote}
            onChange={function (e) {
              setReleaseNote(e.target.value);
            }}
            placeholder="e.g. Passed to invoicing team, awaiting supplier quote..."
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 7,
              border: `1px solid ${"#CFE0DE"}`,
              fontSize: 13,
              fontFamily: "inherit",
              resize: "vertical",
              minHeight: 70,
              boxSizing: "border-box",
              marginBottom: 14,
            }}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              onClick={function () {
                setReleaseGroup(null);
              }}
              style={{
                background: "#eee",
                border: "none",
                borderRadius: 6,
                padding: "7px 16px",
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              Cancel
            </button>
            <button
              onClick={function () {
                if (releaseNote.trim().length >= 4) {
                  releaseGroupTasks(releaseGroup, releaseNote);
                }
              }}
              disabled={releaseNote.trim().length < 4}
              style={{
                background: "#E65100",
                color: "white",
                border: "none",
                borderRadius: 6,
                padding: "7px 16px",
                cursor: releaseNote.trim().length >= 4 ? "pointer" : "not-allowed",
                fontSize: 13,
                fontWeight: 600,
                opacity: releaseNote.trim().length >= 4 ? 1 : 0.5,
              }}
            >
              Release
            </button>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}

export { ToDoPage };
